/**
 * 语音评测注入：给评测题组件提供 speechIns（SpeechSession）、录音倒计时、波形图和统一的错误提示。
 * 组件侧要实现 record_begin_callbcak / evalute_callback / snd_error_notity 三个钩子。
 */
import SpeechSession from '@/speech/session'
import { SpeechErrorCode } from '@/speech/errors'

// 波形图：与旧实现一致，横向最多保留 5000 个采样点，每 8 个原始点取一次均值
const WAVE_POINT_COUNT = 5000
const WAVE_DOM_ID = 'waveform'

export default {
  // speechIns 不放进 data()：放进去 Vue 会把整个会话对象（含录音缓冲数组）做成响应式，
  // 录音时每收一帧都触发依赖通知，纯浪费。它不参与渲染，挂成普通实例属性即可。
  data() {
    return {
      // 播放按钮
      showButtonPlay: false,
      // 暂停按钮
      showButtonPause: false,
      // 结束评测按钮
      showButtonStopRecord: false,
      // 开始评测按钮
      showButtonRecord: false,
      // 加载中按钮
      showButtonLoading: false,
      // 波形图
      showWaveform: false,
      // 倒计时提示文字
      countdownText: '',
      // 录音加载、踩点中文字提示文字
      loadingText: '',
      //录音倒计时
      recordingCountdownTimer: null,
      countdownVal: 0,
    }
  },
  created() {
    this.speechIns = null
    this.__init()
    this.$once('hook:destroyed', () => {
      this.__speechDestroy()
    })
  },
  activated() {
    if (!this.speechIns) {
      this.__init()
    }
    this.$once('hook:deactivated', () => {
      this.__speechDestroy()
    })
  },
  methods: {
    __init() {
      if (this.speechIns != null) return
      this.__speechInit()
    },
    __speechDestroy() {
      this.speechIns && this.speechIns.destroy()
      this.speechIns = null
      this.clearRecordingTimer()
      this.__clearWaveform()
    },
    __speechInit() {
      this.speechIns = new SpeechSession({
        wave_callback: (waveArray) => this.__drawWaveform(waveArray),
        record_begin_callback: () => this.record_begin_callbcak(),
        evaluate_callback: (data) => {
          this.__clearWaveform()
          this.evalute_callback(data)
        },
        error_callback: (err, phase) => {
          if (this.speechIns == null || this.speechIns.status === 'destroy') return
          console.error('[speech] 评测失败', phase, err && err.code, err && err.message)
          this.clearRecordingTimer()
          this.__clearWaveform()
          if (phase === 'init') return
          // 没录到声音没有可"重来"的录音，按普通失败提示并复位按钮
          if (phase === 'processing' && err && err.code !== SpeechErrorCode.EMPTY_AUDIO) {
            // 录音已经结束、送评失败：和以前一样给"重来 / 取消"，重来只是拿同一段录音再送一次
            layer.open({
              type: 0,
              title: '',
              content: this.$t('common_text_server_error'),
              btn: [this.$t('button_common_start_over'), this.$t('button_common_cancel')],
              closeBtn: 0,
              yes: (index) => {
                layer.close(index)
                this.speechIns && this.speechIns.retry()
              },
              btn2: (index) => {
                layer.close(index)
                this.$router.back()
              },
            })
            return
          }
          // 麦克风/浏览器能力问题提示设备文案，其它（凭证、网络、没录到声音）提示网络文案
          const deviceCodes = [
            SpeechErrorCode.MIC_DENIED,
            SpeechErrorCode.NO_GET_USER_MEDIA,
            SpeechErrorCode.NO_WEB_AUDIO,
            SpeechErrorCode.UNSUPPORTED,
          ]
          layer.msg(
            err && deviceCodes.indexOf(err.code) >= 0
              ? this.__micErrorText(err).message
              : this.$t('common_text_none_network')
          )
          this.snd_error_notity({
            errorMsg: err && err.message,
            errorCode: err && err.code,
          })
        },
      })
      this.speechIns.init().catch((err) => {
        const { title, message } = this.__micErrorText(err)
        layer.alert(
          message,
          {
            title: title,
            closeBtn: false,
            move: '.layui-layer-content',
            btn: ['ok'],
          },
          (index) => {
            layer.close(index)
            this.$router.back()
          }
        )
      })
    },
    /** 麦克风/浏览器能力错误 → 提示文案，分支与旧录音器一致 */
    __micErrorText(err) {
      const code = err && err.code
      if (code === SpeechErrorCode.MIC_DENIED) {
        if (err.errorName === 'NotReadableError') {
          return { title: false, message: this.$t('layer_common_notfoundmic') }
        }
        return {
          title: this.$t('media_devices_help_title'),
          message: this.$t('media_devices_help_content'),
        }
      }
      if (
        code === SpeechErrorCode.NO_GET_USER_MEDIA ||
        code === SpeechErrorCode.NO_WEB_AUDIO ||
        code === SpeechErrorCode.UNSUPPORTED
      ) {
        return { title: false, message: this.$t('media_devices_unsupported_browserLabel') }
      }
      return { title: false, message: (err && err.message) || this.$t('common_text_server_error') }
    },
    evalute_callback(res) {},
    snd_error_notity(error) {},
    record_begin_callbcak(e) {},
    cancelRecord() {
      this.clearRecordingTimer()
      this.showWaveform = false
      this.__clearWaveform()
      this.speechIns && this.speechIns.cancel()
    },
    /**
     * 录音倒计时
     */
    clearRecordingTimer() {
      clearInterval(this.recordingCountdownTimer)
      this.recordingCountdownTimer = null
    },
    recordingTimeInterval(cb, time) {
      if (time == null) return
      if (time == -1) {
        time = this.countdownVal
      } else {
        //保存时间，供暂停后使用
        this.countdownVal = time
      }
      this.countdownText = this.$t('evaluate_common_training_remain') + time + 's'
      // 兼容自主命题
      this.$emit('setCountdownText', this.countdownText)
      if (this.recordingCountdownTimer) {
        this.clearRecordingTimer()
      }
      this.recordingCountdownTimer = setInterval(() => {
        try {
          time--
          this.countdownText = this.$t('evaluate_common_training_remain') + time + 's'
          // 兼容自主命题
          this.$emit('setCountdownText', this.countdownText)
          this.countdownVal = time
          if (time <= 0) {
            this.countdownText = ''
            // 兼容自主命题
            this.$emit('setCountdownText', this.countdownText)
            this.clearRecordingTimer()
            cb && cb()
          }
        } catch (e) {
          this.clearRecordingTimer()
        }
      }, 1000)
    },
    /**
     * 波形图：画在 Toolbar 的 #waveform 画布上。每次拿到一帧时域数据就把均值点追加到
     * 队列尾部，超过 WAVE_POINT_COUNT 就从头丢，整体向左滚动。
     */
    __drawWaveform(dataArray) {
      if (!this.__waveCanvas) {
        this.__waveCanvas = document.getElementById(WAVE_DOM_ID)
        if (!this.__waveCanvas) return
        this.__wavePoints = []
      }
      const canvas = this.__waveCanvas
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      const W = canvas.width
      const H = canvas.height
      const points = this.__wavePoints || (this.__wavePoints = [])

      let sum = 0
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i]
        if ((i + 1) % 8 == 0) {
          points.push(sum / 8)
          sum = 0
        }
      }
      if (points.length > WAVE_POINT_COUNT) {
        points.splice(0, points.length - WAVE_POINT_COUNT)
      }

      ctx.clearRect(0, 0, W, H)
      ctx.beginPath()
      ctx.strokeStyle = 'rgba(250, 174, 86, 1)'
      ctx.lineWidth = 1
      const widthRate = W / WAVE_POINT_COUNT
      for (let i = 0; i < points.length; i++) {
        const y = ((points[i] / 128.0) * H) / 2
        const x = i * widthRate
        if (i === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      }
      ctx.stroke()
    },
    __clearWaveform() {
      this.__wavePoints = []
      const canvas = this.__waveCanvas || document.getElementById(WAVE_DOM_ID)
      if (canvas && canvas.getContext) {
        canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height)
      }
      this.__waveCanvas = null
    },
  },
  beforeRouteLeave(to, from, next) {
    this.__speechDestroy()
    next()
  },
}
